import { FirebaseServices } from "./config";
import { callFunction } from "./helpers";
import { trackMessageAdded } from "../analytics";
import {
  ISendMessageRequest,
  ISendMessageResponse,
  IGetMessagesRequest,
  IGetMessagesResponse,
  IMarkMessageReadRequest,
  IMarkMessageReadResponse,
  IMarkMessageUnreadRequest,
  IMarkMessageUnreadResponse,
  ITrashMessageRequest,
  ITrashMessageResponse,
  IRestoreMessageRequest,
  IRestoreMessageResponse,
  IReportInappropriateContentRequest,
  IReportInappropriateContentResponse,
} from "common247";

/**
 * Sends an internal direct or group message.
 * @param fb Firebase services configuration
 * @param request ISendMessageRequest payload
 * @returns Promise resolving to ISendMessageResponse
 */
export async function sendMessage(
  fb: FirebaseServices,
  request: ISendMessageRequest
): Promise<ISendMessageResponse> {
  const result = await callFunction<ISendMessageRequest, ISendMessageResponse>(
    fb,
    "sendMessageFunc",
    request
  );
  if (result && result.status !== false) {
    trackMessageAdded(request.targetId || "direct", request.type || "text");
  }
  return result;
}

/**
 * Retrieves internal messages and unread count for the current user.
 * @param fb Firebase services configuration
 * @param request IGetMessagesRequest payload
 * @returns Promise resolving to IGetMessagesResponse
 */
export async function getMyMessages(
  fb: FirebaseServices,
  request: IGetMessagesRequest
): Promise<IGetMessagesResponse> {
  return await callFunction<IGetMessagesRequest, IGetMessagesResponse>(
    fb,
    "getMyMessagesFunc",
    request
  );
}

/**
 * Marks a specific message as read.
 * @param fb Firebase services configuration
 * @param request IMarkMessageReadRequest payload
 * @returns Promise resolving to IMarkMessageReadResponse
 */
export async function markMessageRead(
  fb: FirebaseServices,
  request: IMarkMessageReadRequest
): Promise<IMarkMessageReadResponse> {
  return await callFunction<IMarkMessageReadRequest, IMarkMessageReadResponse>(
    fb,
    "markMessageReadFunc",
    request
  );
}

/**
 * Marks a specific message as unread.
 * @param fb Firebase services configuration
 * @param request IMarkMessageUnreadRequest payload
 * @returns Promise resolving to IMarkMessageUnreadResponse
 */
export async function markMessageUnread(
  fb: FirebaseServices,
  request: IMarkMessageUnreadRequest
): Promise<IMarkMessageUnreadResponse> {
  return await callFunction<IMarkMessageUnreadRequest, IMarkMessageUnreadResponse>(
    fb,
    "markMessageUnreadFunc",
    request
  );
}

/**
 * Moves a specific message to the user's trash folder.
 * @param fb Firebase services configuration
 * @param request ITrashMessageRequest payload
 * @returns Promise resolving to ITrashMessageResponse
 */
export async function trashMessage(
  fb: FirebaseServices,
  request: ITrashMessageRequest
): Promise<ITrashMessageResponse> {
  return await callFunction<ITrashMessageRequest, ITrashMessageResponse>(
    fb,
    "trashMessageFunc",
    request
  );
}

/**
 * Restores a specific message from the user's trash folder.
 * @param fb Firebase services configuration
 * @param request IRestoreMessageRequest payload
 * @returns Promise resolving to IRestoreMessageResponse
 */
export async function restoreMessage(
  fb: FirebaseServices,
  request: IRestoreMessageRequest
): Promise<IRestoreMessageResponse> {
  return await callFunction<IRestoreMessageRequest, IRestoreMessageResponse>(
    fb,
    "restoreMessageFunc",
    request
  );
}

/**
 * Submits an inappropriate content report to all administrators.
 * @param fb Firebase services configuration
 * @param request IReportInappropriateContentRequest payload
 * @returns Promise resolving to IReportInappropriateContentResponse
 */
export async function reportInappropriateContent(
  fb: FirebaseServices,
  request: IReportInappropriateContentRequest
): Promise<IReportInappropriateContentResponse> {
  return await callFunction<
    IReportInappropriateContentRequest,
    IReportInappropriateContentResponse
  >(fb, "reportInappropriateContentFunc", request);
}
